import React from 'react';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Lock, Crown, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './PermissionGate.css';

/**
 * PermissionGate Component
 * Controls visibility and access to features based on user permissions
 * 
 * Usage Examples:
 * 
 * 1. Page access:
 * <PermissionGate feature="page_statistics" type="page">
 *   <StatisticsContent />
 * </PermissionGate>
 * 
 * 2. Button visibility:
 * <PermissionGate feature="button_export" type="button">
 *   <ExportButton />
 * </PermissionGate>
 * 
 * 3. Action permission:
 * <PermissionGate feature="action_create_offer" type="action">
 *   <CreateOfferForm />
 * </PermissionGate>
 * 
 * 4. Limit check:
 * <PermissionGate feature="limit_offers" type="limit" currentCount={5}>
 *   <AddOfferButton />
 * </PermissionGate>
 */

const PermissionGate = ({
  children,
  feature,
  type = 'page', // 'page' | 'button' | 'action' | 'limit'
  currentCount = 0, // For limit type
  fallback = null, // Custom fallback component
  showUpgradePrompt = true, // Show upgrade message when access denied
  hideWhenDenied = false, // Completely hide content when denied
  onDenied = null // Callback when access denied
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    canAccessPage,
    canPerformAction,
    canSeeButton,
    isLimitReached,
    hasActiveSubscription,
    accountSuspended,
    loading,
    plan,
    fetchPermissions
  } = usePermissions();

  // التحقق من حالة الحساب - من الـ context أو من user.isActive
  // ملاحظة مهمة: نستخدم === للتأكد من القيم الفعلية وليست undefined
  const isAccountSuspended = accountSuspended === true || (user && user.isActive === false);

  // While loading, show loading state or children based on preference
  if (loading) {
    if (type === 'button' || type === 'limit') {
      // For buttons and limits, show children while loading (optimistic)
      return children;
    }
    // For pages, could show loading skeleton
    return children;
  }

  // Check permission based on type
  let hasAccess = true;

  switch (type) {
    case 'page':
      hasAccess = canAccessPage(feature);
      break;
      
    case 'button':
      hasAccess = canSeeButton(feature);
      break;
      
    case 'action':
      hasAccess = canPerformAction(feature);
      break;
      
    case 'limit':
      hasAccess = !isLimitReached(feature, currentCount);
      break;
      
    default:
      hasAccess = true;
  }

  // If access granted, render children
  if (hasAccess) {
    return children;
  }

  // Access denied - trigger callback if provided
  if (onDenied) {
    onDenied(feature, type);
  }

  // If should hide when denied, return null
  if (hideWhenDenied) {
    return null;
  }

  // Return custom fallback if provided
  if (fallback) {
    return fallback;
  }

  // Default behavior based on type
  if (type === 'button') {
    // Hide buttons by default when no access
    return null;
  }

  // Show upgrade prompt for pages and actions
  if (showUpgradePrompt) {
    return (
      <div className="permission-denied">
        <div className="permission-denied-content">
          <div className="permission-denied-icon">
            {isAccountSuspended ? <AlertTriangle size={48} /> : <Lock size={48} />}
          </div>
          <h2>{t('permissions.accessDenied', 'الوصول مقيد')}</h2>
          <p>{t('permissions.noPageAccess', 'ليس لديك صلاحية الوصول لهذه الصفحة')}</p>
          
          {isAccountSuspended ? (
            <div className="upgrade-prompt suspended-account">
              <AlertTriangle size={24} className="warning-icon" />
              <p className="suspended-message">
                <strong>حسابك معلق حالياً</strong>
              </p>
              <p>يرجى التواصل مع الدعم الفني لإعادة تفعيل حسابك</p>
              <a 
                href="https://wa.me/966544091800" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ marginTop: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                تواصل عبر الواتساب: +966 54 409 1800
              </a>
            </div>
          ) : !hasActiveSubscription ? (
            <div className="upgrade-prompt">
              <Crown size={24} className="crown-icon" />
              <p>{t('permissions.noSubscription', 'أنت غير مشترك حالياً. اشترك للاستفادة من جميع الميزات')}</p>
              <div className="upgrade-buttons">
                <button 
                  className="btn btn-primary upgrade-btn"
                  onClick={() => navigate('/settings?tab=subscription')}
                >
                  {t('permissions.subscribe', 'اشترك الآن')}
                </button>
                <button 
                  className="btn btn-outline refresh-btn"
                  onClick={() => {
                    fetchPermissions();
                    window.location.reload();
                  }}
                >
                  {t('permissions.refresh', 'تحديث الصلاحيات')}
                </button>
              </div>
            </div>
          ) : (
            <div className="upgrade-prompt">
              <Crown size={24} className="crown-icon" />
              <p>
                {t('permissions.currentPlan', 'خطتك الحالية:')} <strong>{plan?.nameAr || plan?.name || t('permissions.basicPlan', 'الأساسية')}</strong>
              </p>
              <p>{t('permissions.upgradeMessage', 'قم بترقية خطتك للوصول إلى هذه الميزة')}</p>
              <button 
                className="btn btn-primary upgrade-btn"
                onClick={() => navigate('/settings?tab=subscription')}
              >
                {t('permissions.upgradePlan', 'ترقية الخطة')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No fallback, no upgrade prompt - return null
  return null;
};

/**
 * Higher-order component for protecting routes
 */
export const withPermission = (WrappedComponent, feature, type = 'page') => {
  return function PermissionWrapper(props) {
    return (
      <PermissionGate feature={feature} type={type}>
        <WrappedComponent {...props} />
      </PermissionGate>
    );
  };
};

/**
 * Hook for checking permissions in code
 */
export const useFeatureAccess = () => {
  const permissions = usePermissions();
  
  return {
    canAccess: (feature, type = 'page', currentCount = 0) => {
      switch (type) {
        case 'page':
          return permissions.canAccessPage(feature);
        case 'button':
          return permissions.canSeeButton(feature);
        case 'action':
          return permissions.canPerformAction(feature);
        case 'limit':
          return !permissions.isLimitReached(feature, currentCount);
        default:
          return permissions.hasPermission(feature);
      }
    },
    ...permissions
  };
};

export default PermissionGate;
